import React, { useDeferredValue, useMemo, useState } from "react";
import {
  Button,
  IconButton,
  InputAdornment,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQuery } from "@apollo/client";
import { FiEdit, FiFileText, FiSearch } from "react-icons/fi";
import { IoChevronDownOutline } from "react-icons/io5";
import { GoTrash } from "react-icons/go";
import { Link } from "react-router-dom";
import Loader from "../../Components/Common/Loader";
import { GET_BLOGS } from "../../Utils/Queries";
import { DELETE_BLOG } from "../../Utils/Mutation";
import { useSnackbarHook } from "../../hooks/useSnackbarHook";

const statusOptions = ["All Status", "published", "draft", "unpublished"];
const categoryOptions = ["Category", "All", "Seller", "Buyer"];

const statusStyles = {
  published: "bg-[#eefaf1] text-[#54b46c]",
  draft: "bg-[#fff7df] text-[#e2aa21]",
  unpublished: "bg-[#ffefef] text-[#ff6c6c]",
};

const tableHeadCellSx = {
  borderBottom: "none",
  color: "#303030",
  fontSize: "14px",
  fontWeight: 700,
  fontFamily: "inherit",
  backgroundColor: "#f8f5f2",
};

const tableBodyCellSx = {
  borderBottom: "1px solid #f1ede8",
  color: "#575757",
  fontSize: "14px",
  fontFamily: "inherit",
  py: 2.5,
};

const toTitleCase = (value) =>
  String(value || "")
    .replace(/-/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const formatDate = (value) =>
  new Date(value).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

const getReadingTime = (content) => {
  const words = String(content || "").trim().split(/\s+/).filter(Boolean).length;
  return `${Math.max(1, Math.ceil(words / 200))}m`;
};

const Blogs = () => {
  const [searchValue, setSearchValue] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("All Status");
  const [selectedCategory, setSelectedCategory] = useState("Category");

  const deferredSearchValue = useDeferredValue(searchValue);

  const { handleClick } = useSnackbarHook();

  const { data, loading, refetch } = useQuery(GET_BLOGS, {
    variables: {
      search: deferredSearchValue.trim() || null,
      category: selectedCategory === "Category" ? null : selectedCategory,
      status: selectedStatus === "All Status" ? null : selectedStatus,
    },
    fetchPolicy: "cache-and-network",
  });

  const [deleteBlog, { loading: deleteLoading }] = useMutation(DELETE_BLOG);

  const blogRows = data?.fetchBlogs?.items || [];
  const pagination = data?.fetchBlogs?.pagination;

  const blogStats = useMemo(
    () => [
      { label: "Total Blogs", value: pagination?.totalItems ?? blogRows.length },
      {
        label: "Published",
        value: blogRows.filter((item) => item.status === "published").length,
      },
      {
        label: "Drafts",
        value: blogRows.filter((item) => item.status === "draft").length,
      },
      {
        label: "Unpublished",
        value: blogRows.filter((item) => item.status === "unpublished").length,
      },
    ],
    [blogRows, pagination?.totalItems],
  );

  const handleDeleteBlog = async (blogId) => {
    const shouldDelete = window.confirm(
      "Are you sure you want to delete this blog post?",
    );

    if (!shouldDelete) return;

    try {
      await deleteBlog({
        variables: {
          blogId,
        },
      });
      handleClick("Blog deleted successfully", "success");
      refetch();
    } catch (err) {
      handleClick(err?.message || "Failed to delete blog", "error");
    }
  };

  return (
    <div className="min-h-screen">
      {(loading || deleteLoading) && <Loader />}
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-[30px] font-[800] tracking-[-0.02em] text-[#2f2a25]">
              Blog Posts
            </h1>
            <p className="-mt-4 text-sm text-[#8a837d]">
              Manage articles, drafts, and publication status from one place.
            </p>
          </div>
          <Link to="/dashboard/blog-management/create">
            <Button
              variant="contained"
              sx={{
                borderRadius: "50px",
                height: "40px",
                width: "120px",
                bgcolor: "#5C3069",
              }}
            >
              New Post
            </Button>
          </Link>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          {blogStats.map((item) => (
            <div
              key={item.label}
              className="h-[70px] rounded-[5px] border border-white/70 bg-white px-5 py-6 shadow-[0_12px_40px_rgba(32,24,18,0.04)]"
            >
              <div className="flex items-center gap-3 text-[#6a665f]">
                <FiFileText className="text-[16px]" />
                <span className="text-[14px] font-[400]">{item.label}</span>
              </div>
              <p className="mt-5 text-[25px] font-[700] leading-none text-[#191714]">
                {item.value}
              </p>
            </div>
          ))}
        </div>

        <div className="overflow-hidden rounded-[28px] border border-white/80 bg-white shadow-[0_18px_70px_rgba(30,22,17,0.05)]">
          <div className="border-b border-[#f2ece5] px-4 py-4 md:px-5">
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1fr)_180px_180px]">
              <div className="flex items-center">
                <TextField
                  placeholder="Search by name, category"
                  fullWidth
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  InputProps={{
                    style: {
                      height: "40px",
                      backgroundColor: "#F7F6F6",
                      borderRadius: "4px",
                    },
                    startAdornment: (
                      <InputAdornment position="start">
                        <FiSearch className="text-[18px]" />
                      </InputAdornment>
                    ),
                  }}
                />
              </div>

              <div>
                <TextField
                  select
                  fullWidth
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  SelectProps={{
                    IconComponent: IoChevronDownOutline,
                  }}
                  InputProps={{
                    style: {
                      height: "40px",
                      backgroundColor: "#F7F6F6",
                      borderRadius: "4px",
                    },
                  }}
                >
                  {statusOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {toTitleCase(option)}
                    </MenuItem>
                  ))}
                </TextField>
              </div>

              <div>
                <TextField
                  select
                  fullWidth
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  SelectProps={{
                    IconComponent: IoChevronDownOutline,
                  }}
                  InputProps={{
                    style: {
                      height: "40px",
                      backgroundColor: "#F7F6F6",
                      borderRadius: "4px",
                    },
                  }}
                >
                  {categoryOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </div>
            </div>
          </div>

          <div className="px-4 pb-4 pt-5 md:px-5">
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={tableHeadCellSx}>Title / Slug</TableCell>
                    <TableCell sx={tableHeadCellSx}>Status</TableCell>
                    <TableCell sx={tableHeadCellSx}>Category</TableCell>
                    <TableCell sx={tableHeadCellSx}>Reading time</TableCell>
                    <TableCell sx={tableHeadCellSx}>Date</TableCell>
                    <TableCell sx={tableHeadCellSx}>Action</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {blogRows.map((row) => (
                    <TableRow
                      key={row._id}
                      sx={{
                        "&:hover": {
                          backgroundColor: "#fcfaf8",
                        },
                      }}
                    >
                      <TableCell sx={tableBodyCellSx}>
                        <div className="flex flex-col">
                          <span className="text-[14px] font-[700] text-[#35312c]">
                            {row.title}
                          </span>
                          <span className="mt-1 text-[12px] text-[#8d857f]">
                            /blog/{row.slug}
                          </span>
                        </div>
                      </TableCell>

                      <TableCell sx={tableBodyCellSx}>
                        <span
                          className={`inline-flex min-w-[100px] items-center justify-center rounded-[8px] px-3 py-2 text-[12px] font-[700] ${
                            statusStyles[row.status] || "bg-[#f3f4f6] text-[#6b7280]"
                          }`}
                        >
                          {toTitleCase(row.status)}
                        </span>
                      </TableCell>

                      <TableCell sx={tableBodyCellSx}>{row.category}</TableCell>
                      <TableCell sx={tableBodyCellSx}>
                        {getReadingTime(row.content)}
                      </TableCell>
                      <TableCell sx={tableBodyCellSx}>
                        {formatDate(row.createdAt)}
                      </TableCell>

                      <TableCell sx={tableBodyCellSx}>
                        <div className="flex items-center gap-4">
                          <Link
                            to={`/dashboard/blog-management/edit/${row._id}`}
                            state={{ blog: row }}
                          >
                            <IconButton>
                              <FiEdit className="text-[17px]" />
                            </IconButton>
                          </Link>
                          <IconButton onClick={() => handleDeleteBlog(row._id)}>
                            <GoTrash className="text-[17px] text-[#FF4C5E]" />
                          </IconButton>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {!loading && blogRows.length === 0 && (
              <div className="py-10 text-center text-sm text-[#8d857f]">
                No blogs match the current filters.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Blogs;
